import React from 'react';
import { TrashIcon } from 'lucide-react';
import { AdderSelector, calculateAdderTotal, DOOR_ADDERS } from './AdderSelector';
import type { AdderSelection } from './AdderSelector';
import { calculateBasePrice, getMultiplierLabel } from './pricingUtils';
import { GLASS_OPTIONS, type GlassType, TEMPERED_GLASS_COST } from './WindowRow';
export const SWING_PANEL_OPTIONS = ['X', 'XX', 'XO', 'OX', 'OXO', 'OXX', 'XXO', 'OOX', 'XOO', 'OXXO'] as const;
export type SwingPanelType = (typeof SWING_PANEL_OPTIONS)[number];
export const SWING_DIRECTION_OPTIONS = ['Inswing', 'Outswing'] as const;
export type SwingDirectionType = (typeof SWING_DIRECTION_OPTIONS)[number];
export const HINGE_OPTIONS = ['Left', 'Right'] as const;
export type HingeType = (typeof HINGE_OPTIONS)[number];
export interface SwingDoorData {
  id: string;
  qty: number;
  width: number;
  height: number;
  glass: GlassType;
  tempered: boolean;
  panels: SwingPanelType | '';
  swingDirection: SwingDirectionType | '';
  hinge: HingeType | '';
  adders: AdderSelection;
  included: boolean;
  notes: string;
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
export function getSwingDoorOperationLabel(door: SwingDoorData): string {
  const parts: string[] = [];
  if (door.panels) parts.push(door.panels);
  if (door.swingDirection) parts.push(door.swingDirection);
  if (door.hinge) parts.push(door.hinge);
  return parts.length > 0 ? parts.join(' / ') : '—';
}
export function isSwingDoorOperationComplete(door: SwingDoorData): boolean {
  return door.panels !== '' && door.swingDirection !== '' && door.hinge !== '';
}
interface SwingDoorRowProps {
  door: SwingDoorData;
  index: number;
  productLine: string;
  exteriorColorCost: number;
  onUpdate: (id: string, updates: Partial<SwingDoorData>) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}
export function SwingDoorRow({
  door: win,
  index,
  productLine,
  exteriorColorCost,
  onUpdate,
  onRemove,
  canRemove
}: SwingDoorRowProps) {
  const sqIn = win.width * win.height;
  const sqFt = sqIn / 144;
  const basePrice = calculateBasePrice(win.width, win.height, productLine) * 2;
  const adderCost = calculateAdderTotal(win.adders) + exteriorColorCost + (win.tempered ? TEMPERED_GLASS_COST : 0);
  const unitPrice = basePrice + adderCost;
  const lineTotal = unitPrice * win.qty;
  const isEven = index % 2 === 0;
  const multiplierLabel = getMultiplierLabel(sqIn, productLine);
  const dimmed = !win.included;
  return <tr className={`${isEven ? 'bg-white' : 'bg-gray-50/70'} ${dimmed ? 'opacity-40' : ''}`} data-id="element-121">
      <td className="px-2 py-3 text-center" data-id="element-122">
        <input type="checkbox" checked={win.included} onChange={() => onUpdate(win.id, {
        included: !win.included
      })} aria-label={`Include swing door ${index + 1} in quote`} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-123" />
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-600 text-center" data-id="element-124">
        {index + 1}
      </td>
      <td className="px-3 py-3" data-id="element-125">
        <input type="number" min={1} value={win.qty || ''} onChange={e => onUpdate(win.id, {
        qty: Math.max(1, Number(e.target.value))
      })} placeholder="1" aria-label={`Swing door ${index + 1} quantity`} className="w-16 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-126" />
      </td>
      <td className="px-3 py-3" data-id="element-127">
        <input type="number" min={0} value={win.width || ''} onChange={e => onUpdate(win.id, {
        width: Number(e.target.value)
      })} placeholder="0" aria-label={`Swing door ${index + 1} width in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-128" />
      </td>
      <td className="px-3 py-3" data-id="element-129">
        <input type="number" min={0} value={win.height || ''} onChange={e => onUpdate(win.id, {
        height: Number(e.target.value)
      })} placeholder="0" aria-label={`Swing door ${index + 1} height in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-130" />
      </td>
      <td className="px-4 py-3 text-right" data-id="element-131">
        <span className="text-sm tabular-nums text-gray-700" data-id="element-132">
          {sqFt > 0 ? sqFt.toFixed(1) : '—'}
        </span>
        {multiplierLabel && <span className="block text-[11px] text-gray-400 tabular-nums" data-id="element-133">
            {multiplierLabel}
          </span>}
      </td>
      <td className="px-3 py-3" data-id="element-134">
        <div className="flex flex-col gap-1.5" data-id="element-135">
          <select value={win.panels} onChange={e => onUpdate(win.id, {
          panels: e.target.value as SwingPanelType | ''
        })} aria-label={`Swing door ${index + 1} panels`} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[100px]" data-id="element-136">
            <option value="" data-id="element-137">Panels</option>
            {SWING_PANEL_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-138">
                {opt}
              </option>)}
          </select>
          <select value={win.swingDirection} onChange={e => onUpdate(win.id, {
          swingDirection: e.target.value as SwingDirectionType | ''
        })} aria-label={`Swing door ${index + 1} swing direction`} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[100px]" data-id="element-139">
            <option value="" data-id="element-140">Swing</option>
            {SWING_DIRECTION_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-141">
                {opt}
              </option>)}
          </select>
          <select value={win.hinge} onChange={e => onUpdate(win.id, {
          hinge: e.target.value as HingeType | ''
        })} aria-label={`Swing door ${index + 1} hinge side`} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[100px]" data-id="element-142">
            <option value="" data-id="element-143">Hinge</option>
            {HINGE_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-144">
                {opt}
              </option>)}
          </select>
        </div>
      </td>
      <td className="px-3 py-3" data-id="element-145">
        <select value={win.glass} onChange={e => onUpdate(win.id, {
        glass: e.target.value as GlassType
      })} aria-label={`Swing door ${index + 1} glass obscurity`} className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[110px]" data-id="element-146">
          {GLASS_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-147">
              {opt}
            </option>)}
        </select>
      </td>
      <td className="px-3 py-3" data-id="element-148">
        <AdderSelector adders={win.adders} adderConfig={DOOR_ADDERS} onChange={adders => onUpdate(win.id, {
        adders
      })} data-id="element-149" />
      </td>
      <td className="px-3 py-3" data-id="element-150">
        <input type="text" value={win.notes} onChange={e => onUpdate(win.id, {
        notes: e.target.value
      })} placeholder="Room / notes" aria-label={`Swing door ${index + 1} notes`} className="w-28 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white" data-id="element-151" />
      </td>
      <td className="px-3 py-3 text-center" data-id="element-152">
        {canRemove && <button type="button" onClick={() => onRemove(win.id)} aria-label={`Remove swing door ${index + 1}`} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-153">
            <TrashIcon className="w-4 h-4" data-id="element-154" />
          </button>}
      </td>
    </tr>;
}